/** Slot-rhythm settings + live day preview (Campaigns/Sessions v2 #5).
 *
 * The operator sets the call length, the standard between-call break, an
 * optional per-call slack cushion, and a recurring longer break triggered
 * either after N calls or after X elapsed call-minutes (both supported).
 * A representative day's timeline previews live via POST /slot-preview so
 * the cadence is visible before launch.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";

import type { SlotSettings } from "./slots";

type PreviewEntry = {
  kind: "call" | "slack" | "break" | "long_break";
  index?: number;
  start: string;
  end: string;
  minutes: number;
};
type Preview = {
  entries: PreviewEntry[];
  summary: {
    calls_per_day: number;
    day_start: string;
    day_end: string;
    last_end: string;
  };
};

const KIND_COLOR: Record<PreviewEntry["kind"], string> = {
  call: "bg-nb-mint",
  slack: "bg-amber-400/70",
  break: "bg-slate-500",
  long_break: "bg-sky-500",
};

export function SlotSettingsStep({
  campaignId,
  value,
  onChange,
}: {
  campaignId: string;
  value: SlotSettings;
  onChange: (patch: Partial<SlotSettings>) => void;
}) {
  const { t } = useTranslation();
  const num =
    (k: keyof SlotSettings) => (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ [k]: Number(e.target.value) } as Partial<SlotSettings>);

  const preview = useQuery({
    queryKey: ["campaign-slot-preview", campaignId, value],
    queryFn: async () =>
      (await api.post<Preview>(ApiPaths.campaignSlotPreview(campaignId), value))
        .data,
    enabled: !!campaignId,
    retry: false,
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Num
          label={t("wizard.slot.slotMinutes")}
          value={value.slot_minutes}
          onChange={num("slot_minutes")}
          min={5}
          max={480}
        />
        <Num
          label={t("wizard.slot.breakMinutes")}
          value={value.break_minutes}
          onChange={num("break_minutes")}
          min={0}
          max={240}
        />
        <Num
          label={t("wizard.slot.slackMinutes")}
          value={value.slack_minutes}
          onChange={num("slack_minutes")}
          min={0}
          max={240}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="break_mode">
            {t("wizard.slot.breakMode")}
          </label>
          <select
            id="break_mode"
            className="input"
            value={value.break_mode}
            onChange={(e) =>
              onChange({ break_mode: e.target.value as SlotSettings["break_mode"] })
            }
          >
            <option value="none">{t("wizard.slot.modeNone")}</option>
            <option value="after_n_calls">{t("wizard.slot.modeNCalls")}</option>
            <option value="after_minutes">{t("wizard.slot.modeMinutes")}</option>
          </select>
        </div>
        {value.break_mode === "after_n_calls" && (
          <Num
            label={t("wizard.slot.callsBeforeBreak")}
            value={value.calls_before_break}
            onChange={num("calls_before_break")}
            min={1}
            max={100}
          />
        )}
        {value.break_mode === "after_minutes" && (
          <Num
            label={t("wizard.slot.minutesBeforeBreak")}
            value={value.minutes_before_break}
            onChange={num("minutes_before_break")}
            min={5}
            max={1440}
          />
        )}
        {value.break_mode !== "none" && (
          <Num
            label={t("wizard.slot.longBreakMinutes")}
            value={value.long_break_minutes}
            onChange={num("long_break_minutes")}
            min={0}
            max={480}
          />
        )}
      </div>

      {/* Live day preview */}
      <div className="rounded-md border border-nb-border bg-nb-surface/30 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
            {t("wizard.slot.previewTitle")}
          </h3>
          {preview.data && (
            <span className="text-xs text-nb-text-3">
              {t("wizard.slot.previewSummary", {
                calls: preview.data.summary.calls_per_day,
                start: preview.data.summary.day_start,
                end: preview.data.summary.last_end,
              })}
            </span>
          )}
        </div>

        {preview.isError && (
          <p className="text-xs text-cvp-danger">{t("wizard.slot.previewError")}</p>
        )}
        {preview.data && preview.data.entries.length === 0 && (
          <p className="text-xs text-nb-text-3">{t("wizard.slot.previewEmpty")}</p>
        )}
        {preview.data && preview.data.entries.length > 0 && (
          <>
            <div className="flex h-6 w-full overflow-hidden rounded">
              {preview.data.entries.map((e, i) => (
                <div
                  key={i}
                  className={`${KIND_COLOR[e.kind]} h-full`}
                  style={{ flexGrow: Math.max(1, e.minutes) }}
                  title={`${t(`wizard.slot.kind_${e.kind}`)} · ${e.start}–${e.end}`}
                />
              ))}
            </div>
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-nb-text-3">
              {(["call", "slack", "break", "long_break"] as const).map((k) => (
                <li key={k} className="flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-sm ${KIND_COLOR[k]}`} />
                  {t(`wizard.slot.kind_${k}`)}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function Num({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  min: number;
  max: number;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        className="input"
        value={value}
        min={min}
        max={max}
        onChange={onChange}
      />
    </div>
  );
}
